import * as React from "react";
import { Outlet } from "react-router-dom";
import { ImportWatcher } from "@/pages/inspections/sire-import/ImportWatcher";
import { useDevice } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { useNotificationsStream } from "@/hooks/useNotificationsStream";
import { TourProvider, useTour } from "@/tour/TourContext";
import { hasTourBeenSeen } from "@/tour/tourSeen";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
// import { MobileBottomNav } from "./MobileBottomNav";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { homePathForRole, type Role } from "./nav-config";
import { useTrackHomeIndex } from "@/lib/tab-navigation";

const SIDEBAR_COLLAPSED_KEY = "icheck.sidebar.collapsed";

/**
 * First-login auto-start. Renders nothing — it just kicks off the product tour
 * once for a user who has never seen it, after the shell has painted so the
 * highlight targets exist. Returning users (seen flag set) are never
 * auto-prompted; they can still restart from the profile menu.
 */
function TourAutoStart() {
  const { user } = useAuth();
  const { startTour } = useTour();
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (!user || startedRef.current) return;
    if (hasTourBeenSeen(user)) return;
    // Defer one tick past mount so the sidebar/header anchors are in the DOM.
    // IMPORTANT: mark "started" INSIDE the timeout, not before scheduling it.
    // React StrictMode runs setup → cleanup → setup, and the cleanup cancels
    // this timeout; if we'd set startedRef before scheduling, the second setup
    // would bail (startedRef already true) and the tour would never fire. Setting
    // it only when the timeout actually runs lets the re-run reschedule cleanly
    // (also covers the case where `user` updates within the delay).
    // We also DON'T mark "seen" here — the tour marks itself once its first step
    // renders (and on finish/skip), so a first-time user is never flagged unseen.
    const t = window.setTimeout(() => {
      startedRef.current = true;
      startTour({ auto: true });
    }, 600);
    return () => window.clearTimeout(t);
  }, [user, startTour]);

  return null;
}

export function Layout() {
  const device = useDevice();
  const { user } = useAuth();
  const role = (user?.role ?? "vessel") as Role;
  // Anchor bottom-nav tab navigation to the role's Dashboard (see tab-navigation).
  useTrackHomeIndex(homePathForRole(role));
  const notifications = useNotifications({
    userId: user?.id ? String(user.id) : "",
  });
  // Open the SSE notifications stream for real-time delivery (server→client).
  useNotificationsStream();

  // Persisted desktop collapse state
  const [desktopCollapsed, setDesktopCollapsed] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  });
  React.useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, desktopCollapsed ? "1" : "0");
  }, [desktopCollapsed]);

  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  // Desktop honors the persisted collapse state and can toggle the rail.
  // Mobile AND tablet use the slide-in drawer instead (no persistent rail).
  const sidebarCollapsed = desktopCollapsed;

  // ── Mobile layout ────────────────────────────────────────────
  if (device === "mobile") {
    return (
      // Fixed app shell pinned to the viewport. `fixed inset-0` takes the layout
      // OUT of normal flow, so the document body has nothing to scroll — which
      // stops iOS WKWebView's rubber-band bounce (Android doesn't bounce) that
      // was dragging the sticky Header and breaking its dropdown triggers. Only
      // <main> scrolls; the Header stays put.
      <TourProvider>
        <div className="fixed inset-0 flex flex-col bg-bg">
          <Header
            onOpenMobileMenu={() => setMobileNavOpen(true)}
            notifications={notifications}
          />

          {/* Slide-in drawer for full nav on mobile (since bottom nav is trimmed) */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent side="left" safeTop className="w-65 max-w-[85vw] p-0 bg-sidebar-surface">
              <Sidebar
                role={role}
                collapsed={false}
                showToggle={false}
                onNavigate={() => setMobileNavOpen(false)}
                className="h-full w-full border-r-0"
              />
            </SheetContent>
          </Sheet>

          {/* pb safe-area-inset-bottom: with the web view now under the home
              indicator (overlaysWebView), keep the last content above it.
              env() is 0 on web, so no effect there. */}
          <main data-tour="content" className="flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-[env(safe-area-inset-bottom)]">
            <Outlet />
          </main>

          {/* Renders nothing; keeps a pinned toast on any page while a PDF
              import is being parsed in the background. */}
          <ImportWatcher />

          {/* <MobileBottomNav role={role} /> */}
        </div>
        <TourAutoStart />
      </TourProvider>
    );
  }

  // ── Tablet + Desktop layout ────────────────────────────────── //
  // Desktop (≥xl) shows the persistent sidebar rail. Tablet (md–xl) uses a
  // mobile-style slide-in drawer toggled by the Header hamburger, so the
  // narrower viewport keeps full width for content. Both share the same shell
  // (h-screen + padded <main>) so page layouts tuned for it stay intact.
  const isTablet = device === "tablet";
  return (
    <TourProvider>
      <div className="h-screen overflow-hidden flex bg-bg">
        {isTablet ? (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent side="left" safeTop className="w-65 max-w-[85vw] p-0 bg-sidebar-surface">
              <Sidebar
                role={role}
                collapsed={false}
                showToggle={false}
                onNavigate={() => setMobileNavOpen(false)}
                className="h-full w-full border-r-0"
              />
            </SheetContent>
          </Sheet>
        ) : (
          <Sidebar
            role={role}
            collapsed={sidebarCollapsed}
            onCollapsedChange={setDesktopCollapsed}
            showToggle
          />
        )}

        <div className="flex-1 flex flex-col min-w-0 h-screen">
          <Header
            notifications={notifications}
            onOpenMobileMenu={isTablet ? () => setMobileNavOpen(true) : undefined}
          />
          <main data-tour="content" className="flex-1 px-5 lg:px-7 py-5 lg:py-6 overflow-y-auto overflow-x-hidden">
            <Outlet />
          </main>

          {/* Renders nothing; keeps a pinned toast on any page while a PDF
              import is being parsed in the background. */}
          <ImportWatcher />
        </div>
      </div>
      <TourAutoStart />
    </TourProvider>
  );
}
